"use client"

import React, { memo, useState } from "react"
import Image from "next/image"
import { motion } from "motion/react"
import { cn } from "@/core/utils/utils"
import {
  siClaude,
  siGooglegemini,
  siWhatsapp,
  siStripe,
  siVercel,
  siApple,
} from "simple-icons"

export type MaskVariant = "quantum-bracket" | "monogram-glyph" | "bento-matrix" | "hud-aperture"

export type BrandLogoType = "openai" | "anthropic" | "google" | "whatsapp" | "stripe" | "vercel" | "apple"

interface GeometricMaskedFrameProps {
  image: string
  alt?: string
  variant?: MaskVariant
  badge?: {
    logo?: BrandLogoType
    text: string
    subtext?: string
    statusColor?: string
  }
  glowColor?: "cyan" | "violet" | "emerald" | "amber"
  grayscale?: boolean
  aspectRatio?: "square" | "portrait" | "landscape"
  className?: string
  interactive?: boolean
}

// Official brand vector paths with zero external dependency friction
const BRAND_ICONS: Record<BrandLogoType, { path: string; title: string; defaultColor: string }> = {
  openai: {
    path: "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.5045 4.5045 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.6667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
    title: "OpenAI",
    defaultColor: "#10a37f",
  },
  anthropic: {
    path: siClaude.path,
    title: "Anthropic Claude",
    defaultColor: "#d97706",
  },
  google: {
    path: siGooglegemini.path,
    title: "Google Gemini",
    defaultColor: "#4285f4",
  },
  whatsapp: {
    path: siWhatsapp.path,
    title: "WhatsApp",
    defaultColor: "#25d366",
  },
  stripe: {
    path: siStripe.path,
    title: "Stripe",
    defaultColor: "#635bff",
  },
  vercel: {
    path: siVercel.path,
    title: "Vercel",
    defaultColor: "#ffffff",
  },
  apple: {
    path: siApple.path,
    title: "Apple",
    defaultColor: "#ffffff",
  },
}

export const GeometricMaskedFrame = memo(function GeometricMaskedFrame({
  image,
  alt = "Architecture & Product Proof",
  variant = "quantum-bracket",
  badge,
  glowColor = "cyan",
  grayscale = false,
  aspectRatio = "square",
  className,
  interactive = true,
}: GeometricMaskedFrameProps) {
  const [isHovered, setIsHovered] = useState(false)

  // Unique mask IDs for SVG clipPaths
  const maskId = `geo-mask-${variant}`

  const glowStyles = {
    cyan: "from-cyan-500/20 via-sky-500/10 to-transparent shadow-[0_0_60px_rgba(56,189,248,0.25)]",
    violet: "from-violet-500/25 via-purple-500/10 to-transparent shadow-[0_0_60px_rgba(168,85,247,0.25)]",
    emerald: "from-emerald-500/20 via-teal-500/10 to-transparent shadow-[0_0_60px_rgba(52,211,153,0.25)]",
    amber: "from-amber-500/20 via-orange-500/10 to-transparent shadow-[0_0_60px_rgba(245,158,11,0.25)]",
  }[glowColor]

  return (
    <div
      className={cn("relative group select-none", className)}
      onMouseEnter={() => interactive && setIsHovered(true)}
      onMouseLeave={() => interactive && setIsHovered(false)}
    >
      {/* SVG ClipPath Definitions */}
      <svg className="absolute w-0 h-0 pointer-events-none" aria-hidden="true">
        <defs>
          {/* 1. QUANTUM BRACKET (The Reference Silhouette: Main Squircle + Top-Right Pillar + Left Satellite) */}
          <clipPath id="geo-mask-quantum-bracket" clipPathUnits="objectBoundingBox">
            {/* Satellite Left Lozenge */}
            <rect x="0.08" y="0.28" width="0.09" height="0.22" rx="0.04" ry="0.04" />
            {/* Main Center Body */}
            <path d="M 0.28 0.18 C 0.28 0.14, 0.32 0.10, 0.40 0.10 L 0.60 0.10 C 0.64 0.10, 0.68 0.12, 0.70 0.16 L 0.74 0.24 C 0.76 0.27, 0.80 0.29, 0.84 0.29 L 0.86 0.29 C 0.90 0.29, 0.94 0.33, 0.94 0.38 L 0.94 0.68 C 0.94 0.72, 0.90 0.76, 0.85 0.76 L 0.76 0.76 C 0.72 0.76, 0.69 0.79, 0.69 0.83 L 0.69 0.88 C 0.69 0.92, 0.65 0.95, 0.60 0.95 L 0.22 0.95 C 0.16 0.95, 0.12 0.90, 0.15 0.82 L 0.25 0.50 C 0.26 0.46, 0.28 0.38, 0.28 0.30 Z" />
            {/* Top-Right Extended Pillar */}
            <path d="M 0.76 0.05 C 0.77 0.02, 0.80 0.00, 0.84 0.00 L 0.89 0.00 C 0.93 0.00, 0.96 0.03, 0.96 0.07 L 0.96 0.22 C 0.96 0.26, 0.93 0.29, 0.89 0.29 L 0.81 0.29 C 0.77 0.29, 0.75 0.26, 0.75 0.22 L 0.76 0.05 Z" />
          </clipPath>

          {/* 2. MONOGRAM GLYPH (Angular 'HA' Architectural Cut) */}
          <clipPath id="geo-mask-monogram-glyph" clipPathUnits="objectBoundingBox">
            <path d="M 0.15 0.05 L 0.55 0.05 C 0.65 0.05, 0.72 0.12, 0.72 0.22 L 0.72 0.35 C 0.72 0.42, 0.78 0.48, 0.85 0.48 L 0.92 0.48 C 0.96 0.48, 1.00 0.52, 1.00 0.58 L 1.00 0.85 C 1.00 0.93, 0.93 1.00, 0.85 1.00 L 0.45 1.00 C 0.35 1.00, 0.28 0.93, 0.28 0.83 L 0.28 0.68 C 0.28 0.60, 0.22 0.55, 0.15 0.55 L 0.08 0.55 C 0.04 0.55, 0.00 0.50, 0.00 0.45 L 0.00 0.18 C 0.00 0.10, 0.07 0.05, 0.15 0.05 Z" />
          </clipPath>

          {/* 3. BENTO MATRIX (Multi-Pane Triple Pill Shard) */}
          <clipPath id="geo-mask-bento-matrix" clipPathUnits="objectBoundingBox">
            <rect x="0.00" y="0.00" width="0.62" height="0.58" rx="0.08" ry="0.08" />
            <rect x="0.68" y="0.00" width="0.32" height="0.36" rx="0.08" ry="0.08" />
            <rect x="0.68" y="0.42" width="0.32" height="0.58" rx="0.08" ry="0.08" />
            <rect x="0.00" y="0.64" width="0.62" height="0.36" rx="0.08" ry="0.08" />
          </clipPath>

          {/* 4. HUD APERTURE (Hex-Chamfered Precision Frame) */}
          <clipPath id="geo-mask-hud-aperture" clipPathUnits="objectBoundingBox">
            <path d="M 0.10 0.00 L 0.90 0.00 L 1.00 0.10 L 1.00 0.90 L 0.90 1.00 L 0.10 1.00 L 0.00 0.90 L 0.00 0.10 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Ambient Glow Aura behind the mask */}
      <motion.div
        className={cn(
          "absolute -inset-6 rounded-3xl bg-gradient-to-br opacity-50 blur-3xl transition-opacity duration-700 pointer-events-none",
          glowStyles,
          isHovered ? "opacity-90 scale-105" : "opacity-40"
        )}
        animate={{
          scale: isHovered ? 1.05 : 1,
          opacity: isHovered ? 0.9 : 0.45,
        }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />

      {/* The Clipped Image Container */}
      <motion.div
        className={cn(
          "relative w-full overflow-hidden transition-transform duration-700 ease-out",
          aspectRatio === "square" && "aspect-square",
          aspectRatio === "portrait" && "aspect-[4/5]",
          aspectRatio === "landscape" && "aspect-[16/10]"
        )}
        style={{
          clipPath: `url(#${maskId})`,
          WebkitClipPath: `url(#${maskId})`,
        }}
        animate={{
          scale: isHovered ? 1.02 : 1,
          rotate: isHovered ? -0.5 : 0,
        }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Background Canvas Layer */}
        <div className="absolute inset-0 bg-[#07070a]" />

        {/* The Clipped Visual */}
        <Image
          src={image}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          priority
          className={cn(
            "object-cover object-left-top transition-all duration-700 ease-out",
            grayscale ? "grayscale contrast-125 brightness-95 group-hover:grayscale-0 group-hover:brightness-100" : "contrast-105",
            isHovered && "scale-105"
          )}
        />

        {/* High-Tech Vignette & Scanline Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/80 via-transparent to-[#050507]/20 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
      </motion.div>

      {/* Floating HUD Telemetry Badge with Real Brand Logo */}
      {badge && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={cn(
            "absolute -bottom-3 right-4 sm:right-8 z-20 flex items-center gap-3 px-4 py-2.5 rounded-xl",
            "bg-[#090a10]/90 backdrop-blur-xl border border-white/15",
            "shadow-[0_15px_30px_rgba(0,0,0,0.8),0_0_20px_rgba(56,189,248,0.15)]",
            "transition-all duration-300 group-hover:border-cyan-500/40 group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.9),0_0_30px_rgba(56,189,248,0.3)]"
          )}
        >
          {/* Brand Logo from simple-icons or inline vector */}
          {badge.logo && BRAND_ICONS[badge.logo] && (
            <div
              className="w-5 h-5 flex items-center justify-center rounded-md p-0.5"
              style={{ color: BRAND_ICONS[badge.logo].defaultColor }}
            >
              <svg
                role="img"
                viewBox="0 0 24 24"
                className="w-full h-full fill-current"
                aria-hidden="true"
              >
                <path d={BRAND_ICONS[badge.logo].path} />
              </svg>
            </div>
          )}

          {/* Status Indicator Dot */}
          {!badge.logo && (
            <div
              className="w-2 h-2 rounded-full animate-pulse"
              style={{
                backgroundColor: badge.statusColor || "#10b981",
                boxShadow: `0 0 8px ${badge.statusColor || "#10b981"}`,
              }}
            />
          )}

          <div className="flex flex-col">
            <span className="text-xs font-semibold text-slate-100 tracking-tight leading-none">
              {badge.text}
            </span>
            {badge.subtext && (
              <span className="text-[10px] text-slate-400 font-mono tracking-wider mt-0.5">
                {badge.subtext}
              </span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  )
})
